// Starts the app (Step 27). The API address comes from config.json, which
// each environment's Web stack writes, so one build serves every
// environment. If config.json cannot be read, or the first draw throws, the
// page says so plainly with "Try again" instead of staying blank, and the
// failure is logged without its message (Steps 28 and 29).
import { createRoot } from 'react-dom/client';
import { logClientError, type ClientLog } from './client-log';

export interface AppConfig {
  apiBase: string;
}

function isAppConfig(value: unknown): value is AppConfig {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { apiBase?: unknown }).apiBase === 'string'
  );
}

class NotAConfiguration extends Error {
  override name = 'NotAConfiguration';
}

async function startOrFail(deps: {
  root: HTMLElement;
  loadConfig: () => Promise<unknown>;
  renderApp: (root: HTMLElement, config: AppConfig) => void;
}): Promise<void> {
  const config = await deps.loadConfig();
  if (!isAppConfig(config)) throw new NotAConfiguration('config.json holds no API address');
  deps.renderApp(deps.root, config);
}

export async function startApp(deps: {
  root: HTMLElement;
  loadConfig: () => Promise<unknown>;
  renderApp: (root: HTMLElement, config: AppConfig) => void;
  reload: () => void;
  log?: ClientLog;
}): Promise<void> {
  try {
    await startOrFail(deps);
    return;
  } catch (error) {
    (deps.log ?? logClientError)('APP_START_FAILED', error);
  }
  createRoot(deps.root).render(
    <main className="shell-main">
      <p role="alert" data-testid="app-start-error">
        Caravan could not start. Check your connection and try again.
      </p>
      <button type="button" data-testid="app-start-retry" onClick={deps.reload}>
        Try again
      </button>
    </main>,
  );
}
